import { FormControlLabel, FormGroup, Tooltip, Typography, withStyles } from '@material-ui/core';
import { EventAvailableTwoTone } from '@material-ui/icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';

import { CalendarProfile } from 'src/models/Sessions';

import { ModSwitch } from '../../ModSwitch';
import { CalendarModalTestID } from './CalendarModal.models';
import { useStyles } from './CalendarModal.styles';
import { CalendarModalProfileHeadingButtonGrp } from './CalendarModalProfileHeadingButtonGrp';

interface Props {
  handleCalendarUpdate: (Calendar, CalendarState) => void;
  handleDeleteClick: (DeleteCalendarData) => void;
  handleSyncClick: () => void;
  profile: CalendarProfile;
  working: boolean;
}

const StyledFormControlLabel = withStyles({ label: { fontSize: 12 } })(FormControlLabel);

export const CalendarModalProfileCard: React.FC<Props> = ({
  handleCalendarUpdate,
  handleDeleteClick,
  handleSyncClick,
  profile,
  working,
}) => {
  const { calendars, profileName, providerName } = profile;
  const { showCalendarResyncButton } = useFlags();
  const classes = useStyles();

  return (
    <div className={classes.providerCard} data-testid={CalendarModalTestID.ProfileCard}>
      <div className={classes.providerHeading}>
        <img
          className={classes.providerLogo}
          alt={`${providerName} Logo`}
          src={require(`../../../assets/images/${providerName}-logo.png`)}
        />

        <Typography className={classes.profileName} data-testid={CalendarModalTestID.CalendarName}>
          {profileName}
        </Typography>
        {showCalendarResyncButton && (
          <CalendarModalProfileHeadingButtonGrp
            uuid={profile.uuid}
            profileId={profile.profileId}
            profileName={profile.profileName}
            onDeleteClick={handleDeleteClick}
            onSyncClick={handleSyncClick}
            working={working}
          />
        )}
      </div>

      <div className={classes.calendarList}>
        {calendars.map((calendar) => {
          return (
            <div key={calendar.uuid} className={classes.calendar}>
              <div className={classes.calendarIconWrapper}>
                <EventAvailableTwoTone className={classes.calendarIcon} />
              </div>

              <Typography className={classes.calendarName}>{calendar.calendarName}</Typography>

              {calendar.isReadonly && (
                <Typography className={classes.readonlyPill}>Read Only</Typography>
              )}

              <FormGroup className={classes.calendarActions}>
                <Tooltip title="Check for conflicts to prevent double bookings." placement="left">
                  <StyledFormControlLabel
                    control={
                      <ModSwitch
                        checked={calendar.isActive}
                        onChange={() =>
                          handleCalendarUpdate(calendar, {
                            isActive: !calendar.isActive,
                            isTarget: calendar.isTarget,
                          })
                        }
                        value="Active"
                      />
                    }
                    label="Check for Conflicts"
                    data-testid={CalendarModalTestID.CheckForConflictsSwitch}
                  />
                </Tooltip>

                {!calendar.isReadonly && (
                  <Tooltip title="Add new Client Sessions as they’re scheduled." placement="left">
                    <StyledFormControlLabel
                      control={
                        <ModSwitch
                          checked={calendar.isTarget}
                          onChange={() =>
                            handleCalendarUpdate(calendar, {
                              isTarget: !calendar.isTarget,
                              isActive: calendar.isActive,
                            })
                          }
                          value="Target"
                        />
                      }
                      label="Add to Calendar"
                      data-testid={CalendarModalTestID.AddToCalendarSwitch}
                    />
                  </Tooltip>
                )}
              </FormGroup>
            </div>
          );
        })}
      </div>
    </div>
  );
};
